import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { StatusType, UrgencyType } from '@app/api-module/control-centre-api-client';
import { ModalityType } from '@app/shared/model/shared.model';
import { dispatch, Store } from '@ngxs/store';
import * as WorklistFilterActions from './worklist-filters.actions';
import { WorklistFiltersState } from './worklist-filters.state';

@Component({
    selector: 'app-worklist-filters',
    templateUrl: './worklist-filters.component.html'
})
export class FiltersComponent implements OnInit, OnChanges {
    @Input()
    visible: boolean;

    @Output()
    closePopout = new EventEmitter<void>();

    noPendingFilters$ = this.store.select(WorklistFiltersState.noPendingFilters);

    opening: boolean;
    dirty: boolean;

    urgencyForm: UntypedFormGroup;
    modalityForm: UntypedFormGroup;
    statusForm: UntypedFormGroup;

    @HostListener('document:keydown.escape', ['$event'])
    @HostListener('document:click', ['$event'])
    clickout(event) {
        // Close filters popout if people click outside it, and wasn't just opened.
        if (this.visible && !this.opening && !this.eRef.nativeElement.contains(event.target)) {
            this.close();
        }
    }

    constructor(formBuilder: UntypedFormBuilder, private readonly eRef: ElementRef, private readonly store: Store) {
        this.urgencyForm = formBuilder.group({
            Urgent: new UntypedFormControl(false),
            Routine: new UntypedFormControl(false),
            Trauma: new UntypedFormControl(false),
            Stroke: new UntypedFormControl(false)
        });
        this.modalityForm = formBuilder.group({
            CT: new UntypedFormControl(false),
            MR: new UntypedFormControl(false),
            PlainFilm: new UntypedFormControl(false),
            US: new UntypedFormControl(false),
            NM: new UntypedFormControl(false),
            PT: new UntypedFormControl(false),
            Other: new UntypedFormControl(false)
        });
        this.statusForm = formBuilder.group({
            AwaitingFurtherDetails: new UntypedFormControl(false),
            AwaitingImages: new UntypedFormControl(false),
            TransferringImages: new UntypedFormControl(false),
            ReadyToReport: new UntypedFormControl(false),
            Reporting: new UntypedFormControl(false),
            Reported: new UntypedFormControl(false),
            Cancelled: new UntypedFormControl(false),
            OnHoldMissingClinicalHistory: new UntypedFormControl(false),
            OnHoldMissingImages: new UntypedFormControl(false),
            OnHoldMissingDocumentation: new UntypedFormControl(false),
            OnHoldMissingPriors: new UntypedFormControl(false),
            OnHoldReviewOrderDetails: new UntypedFormControl(false),
            OnHoldMissingPriorImages: new UntypedFormControl(false)
        });
        this.dirty = false;
        this.opening = false;
    }

    ngOnInit(): void {
        this.resetCheckboxes();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.dirty === false) {
            this.resetCheckboxes();
        }
        const visible = changes.visible;
        if (visible) {
            if (visible.previousValue === true && visible.currentValue === false) {
                this.close();
            } else if (!visible.previousValue && visible.currentValue === true) {
                this.opening = true;
                setTimeout(() => {
                    this.opening = false;
                }, 250);
            }
        }
    }

    resetCheckboxes(): void {
        this.store.dispatch(new WorklistFilterActions.ResetPendingFilters());

        const filteredUrgencies = this.store.selectSnapshot(WorklistFiltersState.getFilteredUrgencies);
        const filteredStatuses = this.store.selectSnapshot(WorklistFiltersState.getFilteredStatuses);
        const filteredModalities = this.store.selectSnapshot(WorklistFiltersState.getFilteredModalities);

        for (const enumValue of Object.keys(UrgencyType)) {
            const isChecked = filteredUrgencies.includes(UrgencyType[enumValue]);
            this.urgencyForm.patchValue({ [enumValue]: isChecked });
        }

        for (const enumValue of Object.keys(StatusType)) {
            const isChecked = filteredStatuses.includes(StatusType[enumValue]);
            this.statusForm.patchValue({ [enumValue]: isChecked });
        }

        for (const enumValue of Object.keys(ModalityType)) {
            const isChecked = filteredModalities.includes(ModalityType[enumValue]);
            this.modalityForm.patchValue({ [enumValue]: isChecked });
        }
    }

    changeUrgency(event: MatCheckboxChange, urgencyTypeString: string): void {
        this.dirty = true;
        const urgencyType = UrgencyType[urgencyTypeString];
        if (event.checked) {
            this.addUrgency(urgencyType);
        } else {
            this.removeUrgency(urgencyType);
        }
    }

    addUrgency = dispatch(WorklistFilterActions.AddUrgency);
    removeUrgency = dispatch(WorklistFilterActions.RemoveUrgency);

    changeModality(event: MatCheckboxChange, modalityTypeString: string): void {
        this.dirty = true;
        const modalityType = ModalityType[modalityTypeString];
        if (event.checked) {
            this.addModality(modalityType);
        } else {
            this.removeModality(modalityType);
        }
    }

    addModality = dispatch(WorklistFilterActions.AddModality);
    removeModality = dispatch(WorklistFilterActions.RemoveModality);

    changeStatus(event: MatCheckboxChange, statusTypeString: string): void {
        this.dirty = true;
        const statusType = StatusType[statusTypeString];
        if (event.checked) {
            this.addStatus(statusType);
        } else {
            this.removeStatus(statusType);
        }
    }

    addStatus = dispatch(WorklistFilterActions.AddStatus);
    removeStatus = dispatch(WorklistFilterActions.RemoveStatus);

    close(): void {
        if (this.dirty) {
            this.resetCheckboxes();
        }

        this.dirty = false;
        this.closePopout.emit();
    }

    clearPendingFilters(): void {
        this.store.dispatch(WorklistFilterActions.ClearPendingFilters);
        this.dirty = true;

        for (const enumValue of Object.keys(UrgencyType)) {
            this.urgencyForm.patchValue({ [enumValue]: false });
        }

        for (const enumValue of Object.keys(StatusType)) {
            this.statusForm.patchValue({ [enumValue]: false });
        }

        for (const enumValue of Object.keys(ModalityType)) {
            this.modalityForm.patchValue({ [enumValue]: false });
        }
    }

    submitFilters(): void {
        this.store.dispatch(WorklistFilterActions.ApplyFilters);
        this.close();
    }
}
